import Alpine from 'alpinejs';

/**
 * Gallery lightbox — Alpine data component
 *
 * ## Usage
 * Add `x-data="gallery()"` to the `modgallery-content-body` wrapper.
 * The component scans for `a.image-box` descendants and builds its items list
 * from their `data-photo-id` and `data-photo-src` attributes.
 *
 * ## URL scheme
 * Opening an image pushes `#photo-{item-id}` to the browser history via
 * `history.pushState`. Navigating back/forward updates the lightbox via the
 * `hashchange` event. Deep-linking a `#photo-{id}` URL opens the lightbox
 * on the correct image automatically.
 *
 * ## Animations
 * - First open: CSS keyframes on `> div` (scale 0.88→1 + opacity) and `::backdrop`
 *   (opacity). These restart automatically when the dialog transitions from
 *   display:none to visible — no JS needed.
 * - Navigation (prev/next): Alpine x-transition on <figure> fades opacity 0↔1
 *   over 300ms. The `imgVisible` flag controls this; `_fadeTimer` cancels any
 *   in-progress fade if the user navigates quickly.
 *
 * ## Reuse in other content types
 * 1. Render gallery thumbnails as `<a class="image-box" data-photo-id="{id}" data-photo-src="{full-url}">`.
 * 2. Wrap the gallery block in an element with `x-data="gallery()"`.
 * 3. Add the `<dialog x-ref="dialog">` lightbox markup (see modgallery_content_body.php).
 * 4. Arrow-key navigation is handled at the window level — no extra wiring needed.
 */
export const initGallery = () => {
  Alpine.data('gallery', () => {
    let hashHandler: (() => void) | null = null;

    return {
      isOpen: false as boolean,
      imgVisible: true as boolean,
      items: [] as { id: string; src: string; alt: string }[],
      current: 0,
      _trigger: null as HTMLElement | null,
      _fadeTimer: null as ReturnType<typeof setTimeout> | null,

      get currentSrc(): string {
        return this.isOpen ? (this.items[this.current]?.src ?? '') : '';
      },

      get currentAlt(): string {
        return this.items[this.current]?.alt ?? '';
      },

      get srLabel(): string {
        return this.isOpen
          ? `Zdjęcie ${this.current + 1} z ${this.items.length}: ${this.currentAlt}`
          : '';
      },

      init() {
        this.items = [
          ...(this.$el as HTMLElement).querySelectorAll<HTMLAnchorElement>('a.image-box'),
        ].map((a) => ({
          id: a.dataset.photoId ?? '',
          src: a.dataset.photoSrc ?? '',
          alt: a.getAttribute('aria-label') ?? '',
        }));

        hashHandler = () => this.checkHash();
        this.checkHash();
        window.addEventListener('hashchange', hashHandler);
      },

      destroy() {
        if (hashHandler) window.removeEventListener('hashchange', hashHandler);
        if (this._fadeTimer) clearTimeout(this._fadeTimer);
      },

      checkHash() {
        const m = window.location.hash.match(/^#photo-(\d+)$/);
        if (m) {
          const idx = this.items.findIndex((it) => it.id === m[1]);
          if (idx >= 0) {
            this.show(idx, false);
            return;
          }
        }
        if (this.isOpen) this.closeLightbox(false);
      },

      show(index: number, pushHash = true) {
        if (!this.isOpen) {
          // First open — CSS keyframes handle the entrance animation.
          // imgVisible stays true so no Alpine transition fires on the figure.
          this._trigger = document.activeElement as HTMLElement;
          this.isOpen = true;
          this.current = index;
          (this.$refs['dialog'] as HTMLDialogElement).showModal();
        } else {
          // Already open — fade image out, swap, fade back in.
          if (this._fadeTimer) clearTimeout(this._fadeTimer);
          this.imgVisible = false;
          this._fadeTimer = setTimeout(() => {
            this._fadeTimer = null;
            this.current = index;
            this.imgVisible = true;
          }, 200);
        }
        if (pushHash) history.pushState(null, '', `#photo-${this.items[index].id}`);
      },

      showById(id: string) {
        const idx = this.items.findIndex((it) => it.id === id);
        if (idx >= 0) this.show(idx);
      },

      closeLightbox(clearHash = true) {
        if (this._fadeTimer) {
          clearTimeout(this._fadeTimer);
          this._fadeTimer = null;
        }
        this.isOpen = false;
        this.imgVisible = true; // reset so next open has the image visible
        (this.$refs['dialog'] as HTMLDialogElement).close();
        this._trigger?.focus();
        if (clearHash) history.pushState(null, '', location.pathname + location.search);
      },

      next() {
        this.show((this.current + 1) % this.items.length);
      },

      prev() {
        this.show((this.current - 1 + this.items.length) % this.items.length);
      },
    };
  });
};
